<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import type { FormInstance, FormRules } from "element-plus";
import axios from "axios";
import router from "../router";
import OTPVerification from './OTPVerification.vue';
import AuthSlideshow from './AuthSlideshow.vue';
import type { EpPropMergeType } from 'element-plus/es/utils';

type AccountType = 'retail' | 'business';

interface RuleForm {
  email: string; password: string; contact_name: string;
  business_name: string; registration_number: string; kra_pin: string;
  phone_no: string; accept_terms: boolean;
}

// A letter, nine digits, a letter — the same rule onboarding enforces
// (app/helpers/kraPin.js), applied after spaces are stripped and upper-cased.
const KRA_PIN_RE = /^[A-Z]\d{9}[A-Z]$/;
const normaliseKraPin = (v: string) => String(v || '').replace(/\s+/g, '').toUpperCase();

const route = useRoute();
// ?type=business or ?type=retail lets a link skip the chooser.
const queryType = String(route.query.type || '').toLowerCase();
const accountType = ref<AccountType | null>(
  queryType === 'business' || queryType === 'retail' ? queryType : null,
);
const isBusiness = computed(() => accountType.value === 'business');

const loading = ref(false);
const formSize = ref<EpPropMergeType<StringConstructor, "" | "default" | "small" | "large", unknown>>("default");
const formError = ref("");
const results = ref();
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({
  email: "", password: "", contact_name: "", business_name: "",
  registration_number: "", kra_pin: "", phone_no: "", accept_terms: false,
});
const showOTP = ref(false);
const otpEmail = ref('');

const validateReadTerms = (rule: any, value: any, callback: any) => {
  if (value === false) callback(new Error("Please accept the terms and conditions"));
  else callback();
};
const validatePhone = (rule: any, value: any, callback: any) => {
  if (value === "") callback(new Error("Please enter your phone number"));
  else if (!results.value?.isValid) callback(new Error("Please enter a valid phone number"));
  else callback();
};
const validateKraPin = (rule: any, value: any, callback: any) => {
  const pin = normaliseKraPin(value);
  if (!pin) callback(new Error("Enter the company KRA PIN"));
  else if (!KRA_PIN_RE.test(pin)) callback(new Error("Should look like P051234567X"));
  else callback();
};

// The business-only fields are validated only on the business path.
const rules = computed<FormRules<RuleForm>>(() => ({
  contact_name: [{ required: true, message: "Please enter your name", trigger: "blur" }],
  ...(isBusiness.value ? {
    business_name: [{ required: true, message: "Please enter the company name", trigger: "blur" }],
    registration_number: [
      { required: true, message: "Enter the registration number", trigger: "blur" },
      { min: 2, max: 60, message: "Use 2 to 60 characters", trigger: "blur" },
    ],
    kra_pin: [{ validator: validateKraPin, trigger: "blur" }],
  } : {}),
  email:        [{ required: true, message: "Please enter your email address", trigger: "blur" }],
  phone_no:     [{ validator: validatePhone, trigger: "blur" }],
  password:     [{ required: true, message: "Please enter your password", trigger: "blur" }],
  accept_terms: [{ validator: validateReadTerms, trigger: "blur" }],
}));

const chooseType = (type: AccountType) => {
  accountType.value = type;
  formError.value = "";
};
const changeType = () => {
  accountType.value = null;
  formError.value = "";
  ruleFormRef.value?.clearValidate();
};

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl || loading.value) return;
  formError.value = "";
  await formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      void postData();
    }
  });
};

const postData = async () => {
  const payload: Record<string, unknown> = {
    account_type: accountType.value,
    email: ruleForm.email, password: ruleForm.password,
    name: ruleForm.contact_name, phone: ruleForm.phone_no,
  };
  if (isBusiness.value) {
    payload.company = ruleForm.business_name.trim();
    payload.registration_number = ruleForm.registration_number.trim();
    payload.kra_pin = normaliseKraPin(ruleForm.kra_pin);
  }
  try {
    const response = await axios.post(`${import.meta.env.VITE_ONBOARDING_SERVICE}signup`, payload);
    if (response.status == 200 && response.data.status) {
      otpEmail.value = ruleForm.email;
      showOTP.value = true;
    } else {
      // Was a fixed "Signup failed" that hid the server's reason - including
      // "Email account exists, login instead" and every field error.
      formError.value = response.data?.message || "Signup failed, please try again.";
    }
  } catch (err: any) {
    formError.value = err?.response?.data?.message || "Something went wrong, please try again.";
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="auth-page">
    <AuthSlideshow />
    <div class="auth-right">
      <div class="form-container" v-if="!showOTP">

        <template v-if="!accountType">
          <div class="form-header">
            <h2 class="form-title">Create an account</h2>
            <p class="form-subtitle">Who will you be sending for?</p>
          </div>
          <div class="type-options">
            <button type="button" class="type-option" @click="chooseType('retail')">
              <span class="type-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="3.6"/><path d="M4.8 20c.9-3.6 3.7-5.6 7.2-5.6s6.3 2 7.2 5.6"/></svg>
              </span>
              <span class="type-text">
                <span class="type-title">Personal</span>
                <span class="type-desc">Deliveries in your own name. Sign up with your name, email and phone.</span>
              </span>
            </button>
            <button type="button" class="type-option" @click="chooseType('business')">
              <span class="type-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20V5.5L12 3v17M12 8.5l8 2.5V20M2.5 20h19M7 8h2M7 11.5h2M7 15h2M15 13.5h2M15 16.5h2"/></svg>
              </span>
              <span class="type-text">
                <span class="type-title">Business</span>
                <span class="type-desc">A registered company. You'll add its registration number and KRA PIN, then verify it with documents.</span>
              </span>
            </button>
          </div>
          <div class="bottom-link">
            Already have an account?
            <router-link to="/login" class="nav-link-inline">Sign in</router-link>
          </div>
        </template>

        <template v-else>
          <div class="form-header">
            <button type="button" class="back-btn" @click="changeType">&larr; Change account type</button>
            <h2 class="form-title">{{ isBusiness ? 'Business account' : 'Personal account' }}</h2>
            <p class="form-subtitle">
              {{ isBusiness ? 'Tell us about yourself and the company you send for.' : 'Send deliveries in your own name.' }}
            </p>
          </div>
          <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" @submit.prevent
            label-width="0px" class="auth-form" :size="formSize" v-loading="loading">
            <div v-if="formError" class="error-banner">
              <span class="error-icon">!</span>{{ formError }}
            </div>
            <div class="field-group">
              <label class="field-label">{{ isBusiness ? 'Your full name' : 'Full name' }}</label>
              <el-form-item prop="contact_name">
                <el-input v-model="ruleForm.contact_name" placeholder="Jane Doe" autocomplete="name" />
              </el-form-item>
            </div>
            <template v-if="isBusiness">
              <div class="field-group">
                <label class="field-label">Company name</label>
                <el-form-item prop="business_name">
                  <el-input v-model="ruleForm.business_name" placeholder="Acme Ltd." autocomplete="organization" />
                </el-form-item>
              </div>
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Registration number</label>
                  <el-form-item prop="registration_number">
                    <el-input v-model="ruleForm.registration_number" placeholder="e.g. PVT-ABC1234" autocomplete="off" />
                  </el-form-item>
                </div>
                <div class="field-group">
                  <label class="field-label">Company KRA PIN</label>
                  <el-form-item prop="kra_pin">
                    <el-input v-model="ruleForm.kra_pin" placeholder="e.g. P051234567X" autocomplete="off"
                      maxlength="20" class="kra-input" />
                  </el-form-item>
                </div>
              </div>
            </template>
            <div class="field-group">
              <label class="field-label">Email address</label>
              <el-form-item prop="email">
                <el-input v-model="ruleForm.email" :placeholder="isBusiness ? 'you@company.com' : 'you@example.com'" autocomplete="email" />
              </el-form-item>
            </div>
            <div class="field-group">
              <label class="field-label">Phone number</label>
              <el-form-item prop="phone_no">
                <input type="text" style="display:none" autocomplete="username" />
                <input type="password" style="display:none" autocomplete="new-password" />
                <MazPhoneNumberInput
                  v-model="ruleForm.phone_no" defaultCountryCode="KE"
                  :only-countries="['KE', 'UG', 'TZ', 'NG', 'ZA']"
                  @update="results = $event" :success="results?.isValid"
                  class="phone-input" autocomplete="off"
                />
              </el-form-item>
            </div>
            <div class="field-group">
              <label class="field-label">Password</label>
              <el-form-item prop="password">
                <el-input type="password" v-model="ruleForm.password" placeholder="Create a password" autocomplete="off" show-password />
              </el-form-item>
            </div>
            <div class="field-group terms-group">
              <el-form-item prop="accept_terms">
                <label class="terms-label">
                  <el-checkbox v-model="ruleForm.accept_terms" size="large" />
                  <span class="terms-text">
                    I have read and accepted the
                    <a href="#" @click.prevent class="terms-link">terms and conditions</a>
                  </span>
                </label>
              </el-form-item>
            </div>
            <el-form-item>
              <button type="button" class="submit-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
                <span v-if="!loading">Create account</span><span v-else>Creating account…</span>
              </button>
            </el-form-item>
            <div class="bottom-link">
              Already have an account?
              <router-link to="/login" class="nav-link-inline">Sign in</router-link>
            </div>
          </el-form>
        </template>
      </div>
      <OTPVerification
        v-if="showOTP" :email="otpEmail" context="signup"
        @verified="() => { showOTP = false; router.push('/login'); }"
        @close="() => { showOTP = false; }"
      />
    </div>
  </div>
</template>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.auth-page { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }

.auth-right {
  flex: 1; min-width: 0; display: flex; align-items: center;
  justify-content: center; background: #fafafa; padding: 3rem 2rem;
}

.form-container { width: 100%; max-width: 400px; animation: fadeUp 0.4s ease both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.form-header { margin-bottom: 2.5rem; }
.form-title { font-family: 'DM Sans', sans-serif; font-size: 2rem; font-weight: 700; color: #0f1c2e; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; }
.form-subtitle { font-size: 0.9rem; color: #8a94a6; margin: 0; line-height: 1.6; }

.error-banner { display: flex; align-items: center; gap: 0.6rem; background: #fff5f5; border: 1px solid #fdd; border-radius: 8px; padding: 0.75rem 1rem; font-size: 0.85rem; color: #c0392b; margin-bottom: 1.5rem; }
.error-icon { width: 18px; height: 18px; background: #e74c3c; color: white; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; flex-shrink: 0; }

.field-group { margin-bottom: 1.25rem; }
.label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.field-label { display: block; font-size: 0.82rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem; letter-spacing: 0.01em; }
.forgot-link { font-size: 0.8rem; color: #12284A; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.forgot-link:hover { opacity: 1; }

.auth-form :deep(.ep-input__wrapper),
.auth-form :deep(.el-input__wrapper) { border-radius: 8px !important; border: 1.5px solid #e5e7eb !important; box-shadow: none !important; background: white !important; transition: border-color 0.2s; }
.auth-form :deep(.ep-input__wrapper:hover),
.auth-form :deep(.el-input__wrapper:hover) { border-color: #b0bec5 !important; }
.auth-form :deep(.ep-input__wrapper.is-focus),
.auth-form :deep(.el-input__wrapper.is-focus) { border-color: #12284A !important; }
.auth-form :deep(.ep-form-item),
.auth-form :deep(.el-form-item) { margin-bottom: 0; }
.auth-form :deep(input:-webkit-autofill),
.auth-form :deep(input:-webkit-autofill:hover),
.auth-form :deep(input:-webkit-autofill:focus) { -webkit-box-shadow: 0 0 0px 1000px white inset !important; box-shadow: 0 0 0px 1000px white inset !important; -webkit-text-fill-color: #374151 !important; transition: background-color 5000s ease-in-out 0s; }

.phone-input { width: 100%; }
.auth-form :deep(.m-phone-number-input) { width: 100%; }

.terms-group { margin-top: 0.5rem; }
.terms-label { display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; }
.terms-text { font-size: 0.84rem; color: #374151; line-height: 1.5; margin-top: 2px; }
.terms-link { color: #12284A; text-decoration: underline; }

.submit-btn { width: 100%; height: 46px; background: #12284A; color: white; border: none; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 500; cursor: pointer; margin-top: 0.75rem; transition: background 0.2s, transform 0.1s; }
.submit-btn:hover:not(:disabled) { background: #1a3a6a; }
.submit-btn:active:not(:disabled) { transform: scale(0.99); }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.bottom-link { text-align: center; margin-top: 1.75rem; font-size: 0.87rem; color: #8a94a6; padding-bottom: 1rem; }
.nav-link-inline { color: #12284A; font-weight: 500; text-decoration: none; margin-left: 0.25rem; }
.nav-link-inline:hover { text-decoration: underline; }
.nav-link { font-size: 0.87rem; color: #12284A; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.nav-link:hover { opacity: 1; }

.success-block { text-align: center; animation: fadeUp 0.4s ease both; }
.success-icon { width: 56px; height: 56px; background: #12284A; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 1.5rem; }
.success-block .form-title { margin-bottom: 0.75rem; }
.success-block .form-subtitle { margin-bottom: 2rem; }
.back-link { font-size: 0.87rem; color: #12284A; text-decoration: none; font-weight: 500; }
.back-link:hover { text-decoration: underline; }

@media (max-width: 900px) {
  .auth-right { background: white; }
}

.type-options { display: grid; gap: 0.75rem; margin-bottom: 0.5rem; }
.type-option {
  display: flex; align-items: flex-start; gap: 0.9rem; width: 100%; text-align: left;
  padding: 1rem 1.1rem; background: white; border: 1.5px solid #e5e7eb; border-radius: 10px;
  font-family: inherit; cursor: pointer; transition: border-color 0.15s, background 0.15s;
}
.type-option:hover { border-color: #12284A; background: #fbfcfe; }
.type-option:focus-visible { outline: 2px solid #12284A; outline-offset: 2px; }
.type-icon {
  flex: none; width: 40px; height: 40px; border-radius: 8px; background: #eef2f8; color: #12284A;
  display: inline-flex; align-items: center; justify-content: center;
}
.type-text { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
.type-title { font-size: 0.98rem; font-weight: 600; color: #0f1c2e; }
.type-desc { font-size: 0.84rem; color: #6b7280; line-height: 1.5; }

.back-btn {
  display: inline-block; background: none; border: none; padding: 0; margin: 0 0 1rem 0;
  font-family: inherit; font-size: 0.84rem; color: #12284A; opacity: 0.75; cursor: pointer;
}
.back-btn:hover { opacity: 1; text-decoration: underline; }
.back-btn:focus-visible { outline: 2px solid #12284A; outline-offset: 2px; opacity: 1; }

.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0 0.75rem; }
/* Element Plus positions form errors absolutely, sized for one line. In a
   half-width column a message can wrap, so let it take space instead of
   running into the label below. */
.field-row :deep(.ep-form-item__error),
.field-row :deep(.el-form-item__error) { position: static; padding-top: 4px; }
.kra-input :deep(input) { text-transform: uppercase; letter-spacing: 0.04em; }
.kra-input :deep(input::placeholder) { text-transform: none; letter-spacing: normal; }

@media (max-width: 480px) {
  .field-row { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .form-container { animation: none; }
}
</style>